import { useEffect, useState } from "react";
import Form from "../../../components/form/Form";
import { getUser, useHub } from "../../../core/Global";
import { toastSuccess } from "../../../components/base/Toast";
import Icon from "../../../components/base/Icon";
import ToggleSwitch from "../../../components/form/ToggleSwitch";
import DataItem from "../../../models/DataItem";
import FlexRow from "../../../components/base/FlexRow";
import DropDown from "../../../components/form/DropDown";
import { API_ROUTES } from "../../../core/Constants";
import User from "../models/User";
import { TechWorkingHoursModel } from "../models/TechWorkingHoursModel";
import TimePicker from "../../../components/form/TimePicker";

type Props = {
    tech?: User
}

interface IWeekDay {
    id: number
    name: string
}

export default function TechWorkingHours(props: Props) {

    const hub = useHub()
    const user = getUser()
    const [body, bodySet] = useState<TechWorkingHoursModel>({})
    const [working, workingSet] = useState<boolean>(false)
    const [now, nowSet] = useState<Date>(new Date())
    const [timezones, timezonesSet] = useState<any[]>([])


    const [WeekDays] = useState<IWeekDay[]>([
        {
            id: 1,
            name: "Monday"
        },
        {
            id: 2,
            name: "Tuesday"
        },
        {
            id: 3,
            name: "Wednesday"
        },
        {
            id: 4,
            name: "Thursday"
        },
        {
            id: 5,
            name: "Friday"
        },
        {
            id: 6,
            name: "Saturday"
        },
        {
            id: 0,
            name: "Sunday"
        }
    ])

    useEffect(() => {
        if (props.tech?.id && user) {
            hub.Get(`${API_ROUTES.Technicians}workinghours/${props.tech.id}`, res => {
                bodySet({
                    ...res.data,
                    timezoneId: (res.data.timezoneId ?? props.tech?.timeZoneId ?? user.timeZoneId)
                })
            })
        }
    }, [props.tech, user])

    // Live clock ticking every second for the selected timezone's current time
    useEffect(() => {
        const timer = setInterval(() => nowSet(new Date()), 1000)
        return () => clearInterval(timer)
    }, [])

    const currentTimezoneTime = () => {
        if (!body.timezoneId) return undefined
        try {
            return new Intl.DateTimeFormat("en-GB", {
                timeZone: body.timezoneId,
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hourCycle: "h23"
            }).format(now)
        }
        catch {
            // Fallback: the id is not a valid IANA zone for this browser, use the base UTC offset
            const tz = timezones.find(t => t.id == body.timezoneId)
            if (!tz || tz.offset == null) return undefined
            const target = new Date(now.getTime() + tz.offset * 60000)
            const pad = (n: number) => n.toString().padStart(2, "0")
            return `${pad(target.getUTCHours())}:${pad(target.getUTCMinutes())}:${pad(target.getUTCSeconds())}`
        }
    }



    return (
        <Form id="user-techsetting-paymenttypes" className="form"
            onSubmit={(b) => {

                workingSet(true);

                hub.Put(`${API_ROUTES.Technicians}workinghours/${props.tech?.id}`, body, res => {
                    if (res.success) {
                        toastSuccess("Working hours has been updated successfully!");
                    }
                    workingSet(false);
                })

            }}>
            <div className="card mb-5 mb-xl-10">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">Working Hours</h3>
                    </div>
                    <div className="card-action">
                        <button type="submit" className="btn btn-sm btn-primary" disabled={working}>
                            {
                                working ?
                                    <>
                                        <Icon icon="spinner-third" spin />
                                        <span className="ms-2">Updating...</span>
                                    </>
                                    :
                                    <>
                                        Update Working Hours
                                    </>
                            }
                        </button>
                    </div>
                </div>

                <div className="card-body border-top p-9"
                    style={{
                        maxWidth: 1000
                    }}>

                    <ToggleSwitch label="Technician is available at any time!"
                        raw small className="mb-5"
                        checked={body.alwaysAvailable}
                        onChange={e => bodySet(prev => ({
                            ...prev,
                            alwaysAvailable: e
                        }))} />

                    {
                        !body.alwaysAvailable &&
                        <>
                            <FlexRow className="mt-10" alignItems="center">
                                <div style={{
                                    flex: "1 1 auto",
                                    maxWidth: 600
                                }}>
                                    <DropDown id="drp-redurectrules-timezones" name="timezone"
                                        label="Timezone" block deselect={false}
                                        marginBottom={0}
                                        apiSource={API_ROUTES.Users + "timezones"}
                                        forceSelectValue={body.timezoneId}
                                        onApiSourceFetched={data => timezonesSet(data ?? [])}
                                        onSelect={val => {
                                            bodySet(prev => ({
                                                ...prev,
                                                timezoneId: val
                                            }))
                                        }} />
                                </div>
                                {
                                    currentTimezoneTime() &&
                                    <FlexRow className="ms-4" title="Current Time">
                                        <Icon icon="clock" className="fs-3 text-primary me-2" />
                                        <span className="fs-2 fw-bolder text-primary"
                                            style={{ fontVariantNumeric: "tabular-nums" }}>
                                            {currentTimezoneTime()}
                                        </span>
                                    </FlexRow>
                                }
                            </FlexRow>



                            {
                                WeekDays.map(d => {

                                    const w = body.workingHours?.find(f => f.dayOfWeek == d.id)

                                    return (
                                        <FlexRow key={`weekday-${d.id}`} className="border-top pt-5">

                                            <div className="w-150px">
                                                <ToggleSwitch label={d.name}
                                                    raw small className="mb-5"
                                                    checked={((body.workingHours?.filter(f => f.dayOfWeek == d.id).length ?? 0) > 0)}
                                                    onChange={e => {
                                                        if (e) {
                                                            bodySet(prev => ({
                                                                ...prev,
                                                                workingHours: [...(prev.workingHours ?? []), {
                                                                    dayOfWeek: d.id,
                                                                    fromTime: "08:00",
                                                                    toTime: "18:00"
                                                                }]
                                                            }));
                                                        }
                                                        else {
                                                            bodySet(prev => ({
                                                                ...prev,
                                                                workingHours: (prev.workingHours ?? []).filter(x => x.dayOfWeek !== d.id),
                                                            }));
                                                        }
                                                    }} />
                                            </div>

                                            <div className="w-150px me-2">
                                                <TimePicker id={`time-picker-${d.name}-from`}
                                                    disabled={w == undefined}
                                                    placeholder="From" value={w?.fromTime}
                                                    onTimePicked={t => {
                                                        bodySet(p => ({
                                                            ...p,
                                                            workingHours: (p.workingHours ?? []).map(x =>
                                                                x.dayOfWeek === d.id ? { ...x, fromTime: t.label } : x
                                                            )
                                                        }));
                                                    }}
                                                    onClear={() => {
                                                        bodySet(p => ({
                                                            ...p,
                                                            workingHours: (p.workingHours ?? []).map(x =>
                                                                x.dayOfWeek === d.id ? { ...x, fromTime: "" } : x
                                                            )
                                                        }));
                                                    }}
                                                />
                                            </div>
                                            <div className="w-150px">
                                                <TimePicker id={`time-picker-${d.name}-to`}
                                                    disabled={w == undefined}
                                                    placeholder="To" value={w?.toTime}
                                                    onTimePicked={t => {
                                                        bodySet(p => ({
                                                            ...p,
                                                            workingHours: (p.workingHours ?? []).map(x =>
                                                                x.dayOfWeek === d.id ? { ...x, toTime: t.label } : x
                                                            )
                                                        }));
                                                    }}
                                                    onClear={() => {
                                                        bodySet(p => ({
                                                            ...p,
                                                            workingHours: (p.workingHours ?? []).map(x =>
                                                                x.dayOfWeek === d.id ? { ...x, toTime: "" } : x
                                                            )
                                                        }));
                                                    }}
                                                />
                                            </div>

                                        </FlexRow>
                                    )
                                })
                            }
                        </>
                    }


                </div>

            </div>
        </Form>
    )
}